import { useCallback, useEffect, useRef, useState } from "react";
import { useSeoMeta } from "@unhead/react";
import { ArrowUp, Sparkles, Square, Moon, Sun } from "lucide-react";
import type { NostrEvent } from "@nostrify/nostrify";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { LoginArea } from "@/components/auth/LoginArea";
import { MessageList } from "@/components/MessageList";
import { ModelSelect } from "@/components/ModelSelect";
import { useKindlessAgent, type DraftEvent } from "@/hooks/useKindlessAgent";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useTheme } from "@/hooks/useTheme";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { SYSTEM_PROMPT } from "@/lib/systemPrompt";
import type { Turn, AssistantTurn, AssistantBlock } from "@/lib/chatTypes";

const SUGGESTIONS = [
  "What is kind 30023?",
  "Show me jack's profile",
  "Find recent notes tagged #nostr",
  "Help me draft a kind 1 note saying hello",
  "Look up the most recent zap receipts",
  "Explain reposts vs reactions",
];

function uid() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

const Index = () => {
  useSeoMeta({
    title: "Kindless — an AI-first Nostr browser",
    description:
      "No feeds, no notifications. Just chat with an AI that fetches, explains, renders, and signs any Nostr event on demand.",
  });

  const { run, abort } = useKindlessAgent();
  const { user } = useCurrentUser();
  const { theme, setTheme } = useTheme();
  const [model, setModel] = useLocalStorage<string>("kindless:model", "");
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [turns]);

  const updateAssistant = useCallback(
    (id: string, mutator: (t: AssistantTurn) => AssistantTurn) => {
      setTurns((prev) =>
        prev.map((t) => (t.id === id && t.role === "assistant" ? mutator(t) : t))
      );
    },
    []
  );

  const appendBlock = useCallback(
    (id: string, block: AssistantBlock) => {
      updateAssistant(id, (t) => {
        // Merge consecutive text blocks for clean streaming.
        const last = t.blocks[t.blocks.length - 1];
        if (block.type === "text" && last?.type === "text") {
          const blocks = t.blocks.slice(0, -1);
          blocks.push({ type: "text", text: last.text + block.text });
          return { ...t, blocks };
        }
        return { ...t, blocks: [...t.blocks, block] };
      });
    },
    [updateAssistant]
  );

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || busy) return;
      if (!user) return;
      if (!model) return;

      const userTurn: Turn = { id: uid(), role: "user", text: trimmed };
      const assistantId = uid();
      const assistantTurn: AssistantTurn = {
        id: assistantId,
        role: "assistant",
        blocks: [],
        pending: true,
      };

      // Build history from prior turns (text only) + this user message.
      const history = [...turns, userTurn]
        .map((t) => {
          if (t.role === "user") return { role: "user" as const, content: t.text };
          const txt = t.blocks
            .filter((b): b is { type: "text"; text: string } => b.type === "text")
            .map((b) => b.text)
            .join("");
          return { role: "assistant" as const, content: txt };
        })
        .filter((m) => m.content.trim().length > 0 || m.role === "user");

      setTurns((prev) => [...prev, userTurn, assistantTurn]);
      setInput("");
      setBusy(true);

      try {
        await run(history, SYSTEM_PROMPT, model, {
          onTextChunk: (chunk) => appendBlock(assistantId, { type: "text", text: chunk }),
          onToolStart: (name, args) => appendBlock(assistantId, { type: "tool", name, args }),
          onEvents: (events: NostrEvent[]) =>
            appendBlock(assistantId, { type: "events", events }),
          onDraft: (draft: DraftEvent) => appendBlock(assistantId, { type: "draft", draft }),
        });
      } catch (e) {
        updateAssistant(assistantId, (t) => ({ ...t, error: (e as Error).message }));
      } finally {
        updateAssistant(assistantId, (t) => ({ ...t, pending: false }));
        setBusy(false);
      }
    },
    [busy, user, model, turns, run, appendBlock, updateAssistant]
  );

  const handleStop = () => {
    abort();
    setBusy(false);
  };

  const empty = turns.length === 0;

  return (
    <div className="flex h-screen flex-col bg-gradient-to-b from-background to-muted/30">
      {/* Header */}
      <header className="flex items-center justify-between gap-3 border-b border-border/60 bg-background/70 px-4 py-3 backdrop-blur">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Sparkles className="h-4 w-4" />
          </div>
          <div className="leading-none">
            <div className="text-base font-semibold tracking-tight">Kindless</div>
            <div className="text-[11px] text-muted-foreground">AI-first Nostr browser</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          <LoginArea className="max-w-52" />
        </div>
      </header>

      {/* Conversation */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-3xl px-4 py-6">
          {empty ? (
            <div className="flex min-h-[55vh] flex-col items-center justify-center text-center">
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <Sparkles className="h-7 w-7" />
              </div>
              <h1 className="mb-2 text-3xl font-bold tracking-tight">
                Browse Nostr by asking.
              </h1>
              <p className="mb-8 max-w-md text-muted-foreground">
                No feeds. No notifications. Just a chat that fetches, explains, renders,
                and signs any event — figuring out each kind on demand.
              </p>
              {user ? (
                <div className="flex max-w-xl flex-wrap justify-center gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      className="rounded-full border border-border/70 bg-background/60 px-3.5 py-1.5 text-sm text-foreground/80 transition-colors hover:border-primary/50 hover:text-foreground"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <p className="text-sm text-muted-foreground">
                    Sign in with Nostr to start browsing.
                  </p>
                  <LoginArea />
                </div>
              )}
            </div>
          ) : (
            <MessageList turns={turns} />
          )}
          <div ref={bottomRef} />
        </div>
      </div>

      {/* Composer */}
      <div className="border-t border-border/60 bg-background/70 backdrop-blur">
        <div className="mx-auto w-full max-w-3xl px-4 py-3">
          {!user ? (
            <div className="flex items-center justify-center gap-3 py-2 text-sm text-muted-foreground">
              <span>Sign in to chat with Kindless.</span>
              <LoginArea />
            </div>
          ) : (
            <>
              <div
                className="flex items-end gap-2 rounded-2xl border border-border/70 bg-background px-3 py-2 shadow-sm focus-within:border-primary/50 focus-within:ring-1 focus-within:ring-primary/30"
              >
                <Textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      send(input);
                    }
                  }}
                  placeholder="Ask about any event, kind, or person on Nostr…"
                  rows={1}
                  className="max-h-40 min-h-[2.25rem] resize-none border-0 bg-transparent px-1 py-1.5 text-[15px] shadow-none focus-visible:ring-0"
                />
                {busy ? (
                  <Button
                    size="icon"
                    variant="secondary"
                    className="h-9 w-9 shrink-0 rounded-full"
                    onClick={handleStop}
                    aria-label="Stop"
                  >
                    <Square className="h-4 w-4" />
                  </Button>
                ) : (
                  <Button
                    size="icon"
                    className="h-9 w-9 shrink-0 rounded-full"
                    onClick={() => send(input)}
                    disabled={!input.trim() || !model}
                    aria-label="Send"
                  >
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <div className="mt-1.5 flex items-center justify-between px-1">
                <ModelSelect value={model} onChange={setModel} />
                <a
                  href="https://shakespeare.diy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[11px] text-muted-foreground hover:text-foreground"
                >
                  Vibed with Shakespeare
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Index;
